import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService, { getCurrentUser } from 'services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const applyThemePreference = useCallback((theme) => {
    const nextTheme = theme === 'dark' ? 'dark' : 'light';
    try {
      document.documentElement.dataset.theme = nextTheme;
      localStorage.setItem('theme_preference', nextTheme);
    } catch (error) {
      document.documentElement.dataset.theme = nextTheme;
    }
    return nextTheme;
  }, []);

  const syncThemeFromUser = useCallback((userData) => {
    if (userData?.theme) {
      applyThemePreference(userData.theme);
    }
  }, [applyThemePreference]);

  // Função para verificar se o token está expirado
  const isTokenExpired = () => {
    const token = localStorage.getItem('auth_token');
    const tokenTimestamp = localStorage.getItem('auth_token_timestamp');
    
    if (!token || !tokenTimestamp) {
      return true;
    }

    // Token expira em 12 horas (720 minutos conforme config do backend)
    const expirationTime = 12 * 60 * 60 * 1000; // 12 horas em millisegundos
    const tokenAge = Date.now() - parseInt(tokenTimestamp);
    
    return tokenAge > expirationTime;
  };

  // Função para atualizar dados do usuário do servidor
  const refreshUserData = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      if (response) {
        setUser(response);
        localStorage.setItem('dashboard_user', JSON.stringify(response));
        syncThemeFromUser(response);
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      // Se houver erro, manter dados do localStorage
    }
  }, [syncThemeFromUser]);

  // Função para limpar sessão e redirecionar para login
  const clearSessionAndRedirect = (showExpiredMessage = false) => {
    setUser(null);
    localStorage.removeItem('dashboard_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token_timestamp');
    
    if (showExpiredMessage) {
      setSessionExpired(true);
      // Limpa a mensagem após 20 segundos
      setTimeout(() => setSessionExpired(false), 20000);
    }
    
    // Redireciona para login se não estiver já na página de login
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    // Verificar se há uma flag de sessão expirada (vinda do interceptor da API)
    const sessionExpiredFlag = localStorage.getItem('session_expired');
    if (sessionExpiredFlag === 'true') {
      localStorage.removeItem('session_expired');
      setSessionExpired(true);
      setTimeout(() => setSessionExpired(false), 20000);
      setLoading(false);
      return;
    }

    // Verificar se há um usuário logado no localStorage
    const savedUser = localStorage.getItem('dashboard_user');
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        // Verificar se o token não expirou
        if (isTokenExpired()) {
          clearSessionAndRedirect(true);
        } else {
          // Carregar usuário do localStorage temporariamente
          const tempUser = JSON.parse(savedUser);
          setUser(tempUser);
          syncThemeFromUser(tempUser);
          
          // Buscar dados atualizados do servidor em background
          refreshUserData();
        }
      } catch (error) {
        console.error('Erro ao fazer parse do usuário salvo:', error);
        // Limpar dados corrompidos
        localStorage.removeItem('dashboard_user');
        localStorage.removeItem('dashboard_token');
      }
    }
    setLoading(false);
  }, [refreshUserData, syncThemeFromUser]);

  const login = async (corporate_email, password) => {
    try {
      const {access_token, user} = await authService.login(corporate_email, password);
     
      // Salvar token e timestamp no localStorage
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_token_timestamp', Date.now().toString());
      setUser(user);
      localStorage.setItem('dashboard_user', JSON.stringify(user));
      setSessionExpired(false); // Limpa qualquer mensagem de sessão expirada
      syncThemeFromUser(user);
      
      // Definir contexto da empresa automaticamente após login
      if (user.accessible_companies && user.accessible_companies.length > 0) {
        const firstCompany = user.accessible_companies[0];
        const companyContext = {
          ids: [firstCompany.id],
          companies: [firstCompany]
        };
        localStorage.setItem('horizon_company_context', JSON.stringify(companyContext));
        
        // Disparar evento para notificar outros componentes
        const event = new CustomEvent('company-context-changed', {
          detail: { companyContext }
        });
        window.dispatchEvent(event);
      }
      
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Erro ao autenticar' };
    }
  }

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Erro no logout:', err);
    } finally {
      clearSessionAndRedirect(false);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('dashboard_user', JSON.stringify(updatedUserData));
    syncThemeFromUser(updatedUserData);
  };

  const updateThemePreference = async (theme) => {
    const nextTheme = applyThemePreference(theme);
    if (!user) {
      return nextTheme;
    }
    try {
      await authService.updateTheme(nextTheme);
      const updatedUser = { ...user, theme: nextTheme };
      setUser(updatedUser);
      localStorage.setItem('dashboard_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Erro ao atualizar tema do usu??rio:', error);
    }
    return nextTheme;
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    updateThemePreference,
    refreshUserData,
    loading,
    sessionExpired,
    isTokenExpired,
    clearSessionAndRedirect
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
